<template>
    <div v-if="sidebarStore.hasLiteSidebar || vertical">
        <h5
            class="item-title mb-4"
            data-test="timeline-item-title"
        >
            {{ item.title }}
        </h5>
        <div class="flex flex-col md:flex-row gap-5 ">
            <div class="w-full">
                <SharedEventInfo
                    v-if="isCalendarEvent"
                    :item="item"
                    class="mb-2"
                />
                <div
                    v-else-if="showArchive"
                    class="flex flex-row mb-2"
                >
                    <p
                        class="item-time text-neutral-500 archive-text"
                    >
                        {{ $t('timeline.archive.publish_at', { datetime: formatArchiveDate(item.publishAt, $t('timeline.archive.datetime_format')) }) }}
                    </p>
                    <p
                        v-if="item.publishTo"
                        class="item-time text-neutral-500 ml-3 archive-text"
                    >
                        {{ $t('timeline.archive.publish_to', { datetime: formatArchiveDate(item.publishTo, $t('timeline.archive.datetime_format')) }) }}
                    </p>
                </div>
                <p
                    v-else
                    class="item-time text-neutral-500 mb-2 date-text"
                    data-test="timeline-item-date"
                >
                    {{ $t('timeline.publish_at', { date: formatDate(item.publishAt, $t('timeline.date_format')) }) }}
                </p>
                <timeline-item-description
                    :item="item"
                    data-test="timeline-item-description"
                />
                <image-slider
                    v-if="hasMultipleImages"
                    class="item-image mt-5 w-full cursor-pointer"
                    :image-paths="images"
                />
                <img
                    v-if="hasSingleImage"
                    class="item-image mt-5 w-full cursor-pointer"
                    :alt="item.title"
                    :src="item.image?.data?.path"
                    data-test="timeline-item-image"
                    @click="openModal(item)"
                >
                <file-download-button :item="item" />
            </div>
        </div>
    </div>

    <div v-else>
        <h5
            class="item-title mb-4 text-base"
            data-test="timeline-item-title"
        >
            {{ item.title }}
        </h5>
        <div class="flex flex-col md:flex-row gap-5 items-start">
            <image-slider
                v-if="hasMultipleImages"
                class="item-image no-sidebar w-full md:w-2/5 cursor-pointer"
                :image-paths="images"
            />
            <img
                v-if="hasSingleImage"
                class="item-image no-sidebar w-full md:w-2/5 cursor-pointer"
                :alt="item.title"
                :src="item.image?.data?.path"
                data-test="timeline-item-image"
                @click="openModal(item)"
            >
            <div class="flex flex-col gap-2">
                <SharedEventInfo
                    v-if="isCalendarEvent"
                    :item="item"
                />
                <p
                    v-else
                    class="item-time text-neutral-500 date-text"
                    data-test="timeline-item-date"
                >
                    {{ $t('timeline.publish_at', { date: formatDate(item.publishAt, $t('timeline.date_format')) }) }}
                </p>
                <timeline-item-description :item="item" />
                <file-download-button :item="item" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { formatArchiveDate, formatDate } from '~/utils/helpers';
    import FileDownloadButton from '~/components/timeline/FileDownloadButton.vue';
    import { computed } from 'vue';
    import type { PropType } from 'vue';
    import type { ITimelineItem } from '~/types/types';
    import { useModalStore } from '~/stores/modalStore';
    import { useLiteSidebarStore } from '~/stores/liteSidebarStore';
    import TimelineItemDescription from '~/components/timeline/TimelineItemDescription.vue';
    import ImageSlider from '~/components/common/ImageSlider.vue';
    import { useNavigationStore } from '~/stores/navigationStore';
    import { EMenuType, ETimelineFilterType } from '~/types/enums';
    import SharedEventInfo from '~/components/shared/SharedEventInfo.vue';

    const modalStore = useModalStore();
    const sidebarStore = useLiteSidebarStore();
    const navStore = useNavigationStore();

    const props = defineProps({
        item: {
            type: Object as PropType<ITimelineItem>,
            required: true,
        },
        vertical: {
            type: Boolean,
            default: false
        }
    });

    const hasSingleImage = computed(() => {
        return props.item.images?.data && props.item.images?.data.length === 1;
    });

    const hasMultipleImages = computed(() => {
        return props.item.images?.data && props.item.images?.data.length > 1;
    });

    const images = computed(() => {
        return props.item.images.data.map(image => image.path);
    });

    const showArchive = computed(() => {
        if (!navStore.getLastActiveNavItem) {
            return false;
        }

        return [EMenuType.POSTS_PAGE].includes(navStore.getLastActiveNavItem.type)
            && navStore.getLastActiveNavItem.hasArchive;
    });

    const openModal = (item: ITimelineItem) => {
        if (item.linkUrl) {
            return;
        }

        // prevent opening modal on small screens
        if (window.innerWidth < 500) {
            return;
        }

        modalStore.setModalData(
            item.title,
            item.images?.data.map(image => image.path),
            true,
        );
    };

    const isCalendarEvent = computed(() =>
        [props.item?.postType, props.item?.type].includes(ETimelineFilterType.CALENDAR_EVENT)
    );
</script>

<style>
    .item-image {
        object-fit: contain;
    }

    .item-image.no-sidebar {
        min-width: 40%;
    }

    .archive-text,
    .date-text {
        font-size: 0.825rem;
    }
</style>
